import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useRef, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useLocale } from "@/contexts/IntlContext";
import { Button } from "@/components/ui/button.tsx";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ExerciseLayout } from "@/components/ui/exercise-layout";
import book1 from "@/assets/book-1.png";
import book2 from "@/assets/book-2.png";
import book3 from "@/assets/book-3.png";
import book4 from "@/assets/book-4.png";
import book5 from "@/assets/book-5.png";
import book6 from "@/assets/book-6.png";
import book7 from "@/assets/book-7.png";
import book8 from "@/assets/book-8.png";
import book9 from "@/assets/book-9.png";

export const Route = createFileRoute(
  "/(content)/adults/exercises/(shared)/diary-adults/diary-template"
)({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: "Choose Your Diary Template - HAMD",
      },
    ],
  }),
  beforeLoad: () => ({
    title: "weakness.exercises.diary.template",
    navbar: {
      showTitle: true,
    },
  }),
});

const notebooks = [
  {
    id: 1,
    image: book1,
    gradient: "linear-gradient(135deg, #F8D950 0%, #FDFDFC 100%)",
  },
  {
    id: 2,
    image: book2,
    gradient: "linear-gradient(135deg, #E3A6B3 0%, #9D3A4F 100%)",
  },
  {
    id: 3,
    image: book3,
    gradient: "linear-gradient(135deg, #0288D1 0%, #E1F5FE 100%)",
  },
  {
    id: 4,
    image: book4,
    gradient: "linear-gradient(135deg, #F6AB4C 0%, #FCB19E 100%)",
  },
  {
    id: 5,
    image: book5,
    gradient: "linear-gradient(135deg, #663A4F 0%, #FFE36C 100%)",
  },
  {
    id: 6,
    image: book6,
    gradient: "linear-gradient(135deg, #FCD275 0%, #FFFFFD 100%)",
  },
  {
    id: 7,
    image: book7,
    gradient: "linear-gradient(135deg, #9FD64F 0%, #BE1C2E 100%)",
  },
  {
    id: 8,
    image: book8,
    gradient: "linear-gradient(135deg, #414042 0%, #DEDEDE 100%)",
  },
  {
    id: 9,
    image: book9,
    gradient: "linear-gradient(135deg, #EFB136 0%, #F9ADBE 100%)",
  },
];

function RouteComponent() {
  const [selectedNotebook, setSelectedNotebook] = useState<number | null>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { locale } = useLocale();
  const isRTL = locale === "ar";

  const handleNotebookSelect = (id: number) => {
    setSelectedNotebook(id);
  };

  const handleChooseDiary = () => {
    if (selectedNotebook) {
      navigate({
        to: "/adults/exercises/diary-adults/exercise",
        search: { templateId: selectedNotebook },
      });
    }
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const { scrollLeft, scrollWidth, clientWidth } = container;

      if (isRTL) {
        const scrollableWidth = scrollWidth - clientWidth;
        const currentScroll = Math.abs(scrollLeft);
        setShowLeftArrow(currentScroll > 10);
        setShowRightArrow(currentScroll < scrollableWidth - 10);
      } else {
        setShowLeftArrow(scrollLeft > 10);
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const { scrollWidth, clientWidth } = container;
        const hasOverflow = scrollWidth > clientWidth;

        if (isRTL) {
          container.scrollLeft = container.scrollWidth;
          setShowLeftArrow(false);
          setShowRightArrow(hasOverflow);
        } else {
          container.scrollLeft = 0;
          setShowLeftArrow(false);
          setShowRightArrow(hasOverflow);
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isRTL]);

  const scrollToDirection = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = 300;
      const currentScroll = container.scrollLeft;
      let newScrollLeft;

      if (isRTL) {
        if (direction === "left") {
          newScrollLeft = currentScroll + scrollAmount;
        } else {
          newScrollLeft = currentScroll - scrollAmount;
        }
      } else {
        if (direction === "left") {
          newScrollLeft = currentScroll - scrollAmount;
        } else {
          newScrollLeft = currentScroll + scrollAmount;
        }
      }

      container.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      });
    }
  };

  return (
    <ExerciseLayout
      contentClassName="py-7.5 space-y-18"
      actions={
        <Button
          className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleChooseDiary}
          disabled={!selectedNotebook}
          variant="default"
        >
          <FormattedMessage id="diary.template.chooseDiary" />
        </Button>
      }
    >
        <p className="text-small">
          <FormattedMessage id="diary.template.intro" />
        </p>

      <div className="relative">
        {showLeftArrow && (
          <button
            onClick={() => scrollToDirection("left")}
            className={`absolute ${isRTL ? "right-0" : "left-0"} top-1/2 -translate-y-1/2 z-10 bg-secondary shadow-lg rounded-full p-2 transition-all duration-200`}
            aria-label={isRTL ? "Scroll right" : "Scroll left"}
          >
            <ChevronLeft className="w-6 h-6 text-dark-gray" />
          </button>
        )}

        {showRightArrow && (
          <button
            onClick={() => scrollToDirection("right")}
            className={`absolute ${isRTL ? "left-0" : "right-0"} top-1/2 -translate-y-1/2 z-10 bg-secondary shadow-lg rounded-full p-2 transition-all duration-200`}
            aria-label={isRTL ? "Scroll left" : "Scroll right"}
          >
            <ChevronRight className="w-6 h-6 text-dark-gray" />
          </button>
        )}

        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide px-2"
        >
          {notebooks.map((notebook) => {
            return (
              <div
                key={notebook.id}
                className="cursor-pointer"
                onClick={() => handleNotebookSelect(notebook.id)}
              >
                <div
                  className={`
                    w-73 h-[350px] rounded-2xl p-4 flex flex-col items-center justify-center
                    transition-all duration-300
                    ${
                      selectedNotebook === notebook.id &&
                      "ring-2 ring-primary ring-offset-2 scale-97"
                    }
                  `}
                  style={{ background: notebook.gradient }}
                >
                  <div className="flex items-center justify-center">
                    <img
                      src={notebook.image}
                      alt={`Notebook ${notebook.id}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </ExerciseLayout>
  );
}
